import React, { useEffect, useState } from "react";
import { FiDownload, FiEye, FiChevronLeft, FiChevronRight, FiClock, FiCalendar } from "react-icons/fi";
import { toast } from "sonner";
import ApiServices from "../services/apiservices";

export default function OrderListView() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filtering States
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");

  // Hardcoded corporate calendar range to support server lookups 
  const dynamicYearsRange = ["All", "2024", "2025", "2026", "2027"];

  const monthsList = [
    { value: "01", name: "January" },
    { value: "02", name: "February" },
    { value: "03", name: "March" },
    { value: "04", name: "April" },
    { value: "05", name: "May" },
    { value: "06", name: "June" },
    { value: "07", name: "July" },
    { value: "08", name: "August" },
    { value: "09", name: "September" },
    { value: "10", name: "October" },
    { value: "11", name: "November" },
    { value: "12", name: "December" }
  ];

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const endpoint = `/orders/fetch.php?page=${page}&limit=10&year=${selectedYear}&month=${selectedMonth}`;
      const res = await ApiServices.getRequest(endpoint);
      
      if (res && res.success) {
        setOrders(res.orders || []);
        setTotalPages(res.totalPages || 1);
      }
    } catch (error) {
      toast.error("Failed to fetch orders list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page, selectedYear, selectedMonth]);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setPage(1);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setPage(1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-NG", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="w-full min-h-screen bg-white font-sans">
      
      {/* Hero Banner Section */}
      <div className="relative bg-[#14532d] text-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}> 
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className="lg:w-2/3">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight uppercase tracking-tight">
                BEDC MYTO <span className="text-yellow-400">Orders</span>
              </h1>
              <div className="w-20 h-1.5 bg-yellow-400 rounded-full mb-6"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Filter Toolbar Section */}
        <div className="bg-gray-50 p-4 md:p-5 rounded-2xl border border-gray-100 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700 flex-shrink-0">
            <FiCalendar className="text-green-700 text-base" /> Filter Framework:
          </div>
          <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:flex sm:items-center">
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-bold uppercase tracking-wider bg-white text-gray-700 outline-none focus:ring-2 focus:ring-green-700"
            >
              <option value="All">All Years</option>
              {dynamicYearsRange.filter(y => y !== "All").map(y => <option key={y} value={y}>{y}</option>)}
            </select>

            <select
              value={selectedMonth}
              onChange={handleMonthChange}
              className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-bold uppercase tracking-wider bg-white text-gray-700 outline-none focus:ring-2 focus:ring-green-700"
            >
              <option value="All">All Months</option>
              {monthsList.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="w-full py-20 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-green-700 border-t-yellow-400 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-xs font-bold text-gray-400 tracking-widest uppercase">Refreshing Orders...</p>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center text-gray-400 py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="text-4xl mb-4">📂</div>
            <p className="text-base font-bold uppercase tracking-tight">
              No official regulatory orders found matching the chosen search criteria.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {orders.map((order) => (
              <div 
                key={order.id} 
                className="group block bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center text-green-700 flex-shrink-0 font-extrabold text-sm uppercase">
                    PDF
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1 uppercase tracking-tight group-hover:text-green-700 transition-colors line-clamp-1">
                      {order.title}
                    </h3>
                    <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 mt-1">
                      <FiClock className="text-green-700 text-sm" />
                      Uploaded on: {order.createdAt ? formatDate(order.createdAt) : "N/A"}
                    </p>
                  </div>
                </div>

                {/* View / Download Action Buttons */}
                <div className="flex items-center gap-2 w-full md:w-auto border-t md:border-none pt-4 md:pt-0">
                  <a 
                    href={order.pdfFileUrl} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="flex-1 md:flex-initial flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-[11px] font-bold uppercase tracking-wider rounded-xl hover:bg-black hover:text-white transition-colors"
                  >
                    <FiEye /> View
                  </a>
                  <a 
                    href={`${order.pdfFileUrl}?download=true`} 
                    download
                    className="flex-1 md:flex-initial flex items-center justify-center gap-2 px-4 py-2 bg-green-700 text-white text-[11px] font-bold uppercase tracking-wider rounded-xl hover:bg-yellow-400 hover:text-black transition-colors"
                  >
                    <FiDownload /> Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={() => setPage(p => Math.max(p - 1, 1))}
              disabled={page === 1}
              className="p-3 bg-white border rounded-xl text-gray-600 disabled:opacity-40 hover:bg-gray-100 transition shadow-sm"
            >
              <FiChevronLeft size={20} />
            </button>
            <span className="text-xs font-black text-gray-500 uppercase tracking-widest">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(p + 1, totalPages))}
              disabled={page === totalPages}
              className="p-3 bg-white border rounded-xl text-gray-600 disabled:opacity-40 hover:bg-gray-100 transition shadow-sm"
            >
              <FiChevronRight size={20} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}